// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type MontecarloSimulationResp } from '@shared/data-access/interfaces/api/ai/montecarlo';
import { type PopupsName } from '@shared/ui-kit/components';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { numberToFixed } from '@shared/ui-kit/utils';
import { type TFunction } from 'i18next';
import { isNullOrUndefined } from '@shared/utils';

const LABEL_KEYS: Partial<Record<keyof MontecarloSimulationResp, string>> = {
  average_returns: 'common.annualized-return',
  volatility: 'common.annualized-volatility',
  sharpe: 'common.sharpe-ratio',
  sortino: 'common.sortino-ratio',
  max_drawdawns: 'common.max-drawdown',
};

export const simulationTableRows = (
  data: MontecarloSimulationResp[],
  accessor: keyof MontecarloSimulationResp,
  t: TFunction,
) => {
  const formatValue = (
    value: number | undefined,
    accessor: keyof MontecarloSimulationResp,
  ): string => {
    if (isNullOrUndefined(value)) {
      return '-';
    }

    switch (accessor) {
      case 'average_returns':
      case 'volatility':
      case 'max_drawdawns':
        return `${numberToFixed(value * 100)}%`;

      case 'sharpe':
      case 'sortino':
        return String(numberToFixed(value));

      default:
        return '-';
    }
  };

  const popups: Partial<Record<keyof MontecarloSimulationResp, PopupsName>> = {
    volatility: 'simulation.expectedVolatility',
    sharpe: 'kpis.sharpeRatio',
    sortino: 'simulation.sortinoRatio',
    max_drawdawns: 'kpis.maxDrawdown',
  };

  return {
    label: LABEL_KEYS[accessor] ? t(LABEL_KEYS[accessor]!) : '',
    popup: popups[accessor],
    percentile_10th: formatValue(data[0]?.[accessor] as number, accessor),
    percentile_25th: formatValue(data[1]?.[accessor] as number, accessor),
    percentile_50th: formatValue(data[2]?.[accessor] as number, accessor),
    percentile_75th: formatValue(data[3]?.[accessor] as number, accessor),
    percentile_90th: formatValue(data[4]?.[accessor] as number, accessor),
  };
};

export const simulationEndingBalanceRow = (
  data: MontecarloSimulationResp[],
  t: TFunction,
) => {
  return {
    label: t('common.ending-balance'),
    popup: undefined,
    percentile_10th:
      formatToMoney(data[0]?.total_position?.at(-1) as number) ?? '-',
    percentile_25th:
      formatToMoney(data[1]?.total_position?.at(-1) as number) ?? '-',
    percentile_50th:
      formatToMoney(data[2]?.total_position?.at(-1) as number) ?? '-',
    percentile_75th:
      formatToMoney(data[3]?.total_position?.at(-1) as number) ?? '-',
    percentile_90th:
      formatToMoney(data[4]?.total_position?.at(-1) as number) ?? '-',
  };
};
